import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useOperator } from '@/lib/operator-context';
import {
  CustomerProfile,
  getEngagementColor,
  getTierColorClasses,
  CustomerPreferences,
  BookingListItemClient,
} from '@/lib/crm/client-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Loader2,
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  MessageSquare,
  Lock,
  User,
  DollarSign,
  TrendingUp,
  Clock,
  X,
  Plus,
  Edit2,
  Save,
  XCircle,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

/**
 * Mask phone number for display
 */
function maskPhone(phone: string, showFull: boolean): string {
  if (showFull) return phone;
  const parts = phone.split(' ');
  if (parts.length >= 3) {
    const lastPart = parts[parts.length - 1];
    const masked = lastPart.replace(/^(\d)(\d{4})(-?\d{4})$/, '$1****$3');
    parts[parts.length - 1] = masked;
    return parts.join(' ');
  }
  return phone.replace(/(\d{5})(\d{4})$/, '*****$2');
}

/**
 * Get status badge variant
 */
function getStatusBadge(status: string): { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string } {
  switch (status) {
    case 'COMPLETED':
      return { variant: 'default', label: 'Concluido' };
    case 'CONFIRMED':
      return { variant: 'secondary', label: 'Confirmado' };
    case 'PENDING':
      return { variant: 'outline', label: 'Pendente' };
    case 'CANCELLED':
      return { variant: 'destructive', label: 'Cancelado' };
    case 'NO_SHOW':
      return { variant: 'destructive', label: 'Nao Compareceu' };
    default:
      return { variant: 'outline', label: status };
  }
}

function CustomerProfilePage() {
  const { id: customerId } = Route.useParams();
  const navigate = useNavigate();

  const { operator, permissions, isLoading: authLoading } = useOperator();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit states
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [newTag, setNewTag] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Profile edit states (name, email)
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedEmail, setEditedEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Check if user can view full PII
  const canViewFullPII = permissions?.canViewFullPII ?? false;

  // API call helper with retry logic
  const fetchWithAuth = useCallback(async (url: string, options?: RequestInit) => {
    const token = localStorage.getItem('auth_token');
    return fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'X-Operator-ID': operator?.id || '',
        ...options?.headers,
      },
    });
  }, [operator?.id]);

  // Retry helper for transient failures
  const fetchWithRetry = useCallback(async (url: string, options?: RequestInit, maxRetries = 2): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetchWithAuth(url, options);

        // If successful or it's a genuine 404 after all retries, return
        if (response.ok || (response.status === 404 && attempt === maxRetries)) {
          return response;
        }

        // If it's not the last attempt and we got a 404 or 5xx, retry after delay
        if (attempt < maxRetries && (response.status === 404 || response.status >= 500)) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
          continue;
        }

        return response;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Network error');
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }

    throw lastError || new Error('Failed to fetch after retries');
  }, [fetchWithAuth]);

  // Load customer profile via API with retry logic
  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithRetry(`/api/operator/customers/${customerId}/profile`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        const errMsg = typeof data.error === 'string' ? data.error : data.error?.message || 'Customer not found';
        setError(errMsg);
        return;
      }

        // Parse dates from ISO strings
        const profile: CustomerProfile = {
          ...data.profile,
          customer: {
            ...data.profile.customer,
            firstBookingAt: data.profile.customer.firstBookingAt ? new Date(data.profile.customer.firstBookingAt) : null,
            lastBookingAt: data.profile.customer.lastBookingAt ? new Date(data.profile.customer.lastBookingAt) : null,
            lastInteraction: data.profile.customer.lastInteraction ? new Date(data.profile.customer.lastInteraction) : null,
            createdAt: data.profile.customer.createdAt ? new Date(data.profile.customer.createdAt) : new Date(),
          },
          bookings: data.profile.bookings.map((b: { scheduledAt: string } & Record<string, unknown>) => ({
            ...b,
            scheduledAt: new Date(b.scheduledAt),
          })),
          spendingStats: {
            ...data.profile.spendingStats,
            lastPaymentDate: data.profile.spendingStats.lastPaymentDate ? new Date(data.profile.spendingStats.lastPaymentDate) : null,
          },
          serviceHistory: data.profile.serviceHistory.map((s: { lastUsed: string } & Record<string, unknown>) => ({
            ...s,
            lastUsed: new Date(s.lastUsed),
          })),
        };

        setProfile(profile);
        setEditedNotes(profile.customer.notes || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load customer');
      } finally {
        setLoading(false);
      }
  }, [customerId, fetchWithRetry]);

  useEffect(() => {
    if (!operator || !customerId) return;
    loadProfile();
  }, [operator, customerId, loadProfile]);

  const formatDate = (date: Date | null) => {
    if (!date) return '-';
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const handleSaveNotes = async () => {
    if (!profile) return;
    setSavingNotes(true);
    try {
      const response = await fetchWithAuth(`/api/operator/customers/${profile.customer.id}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'updateNotes', notes: editedNotes }),
      });
      if (response.ok) {
        setProfile({
          ...profile,
          customer: { ...profile.customer, notes: editedNotes },
        });
        setIsEditingNotes(false);
      }
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    if (!editedName.trim()) return;
    setSavingProfile(true);
    try {
      const response = await fetchWithAuth(`/api/operator/customers/${profile.customer.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: editedName.trim(),
          email: editedEmail.trim() || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setProfile({
          ...profile,
          customer: {
            ...profile.customer,
            name: data.data.name || profile.customer.name,
            email: data.data.email || undefined,
          },
        });
        setIsEditingProfile(false);
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAddTag = async () => {
    if (!profile || !newTag.trim()) return;
    try {
      const response = await fetchWithAuth(`/api/operator/customers/${profile.customer.id}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'addTag', tag: newTag.trim() }),
      });
      if (response.ok) {
        setProfile({
          ...profile,
          customer: {
            ...profile.customer,
            tags: [...profile.customer.tags, newTag.trim()],
          },
        });
        setNewTag('');
        setIsAddingTag(false);
      }
    } catch (err) {
      console.error('Failed to add tag:', err);
    }
  };

  const handleRemoveTag = async (tag: string) => {
    if (!profile) return;
    try {
      const response = await fetchWithAuth(`/api/operator/customers/${profile.customer.id}/profile`, {
        method: 'PUT',
        body: JSON.stringify({ action: 'removeTag', tag }),
      });
      if (response.ok) {
        setProfile({
          ...profile,
          customer: {
            ...profile.customer,
            tags: profile.customer.tags.filter(t => t !== tag),
          },
        });
      }
    } catch (err) {
      console.error('Failed to remove tag:', err);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!operator) {
    return null;
  }

  if (!permissions?.canSearchCustomers) {
    return (
      <Card className="text-center py-12">
        <CardContent className="flex flex-col items-center gap-4">
          <Lock className="h-12 w-12 text-muted-foreground" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground max-w-md">
            Your role does not have permission to view customers.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error || !profile) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate({ to: '/operator/customers' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Customers
        </Button>
        <Card className="text-center py-12">
          <CardContent className="flex flex-col items-center gap-4">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Customer Not Found</h2>
            <p className="text-muted-foreground max-w-md">
              {error || 'The requested customer could not be found. They may have been deleted or the link may be incorrect.'}
            </p>
            <div className="flex gap-3 mt-2">
              <Button
                variant="outline"
                onClick={() => loadProfile()}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  'Retry'
                )}
              </Button>
              <Button onClick={() => navigate({ to: '/operator/customers' })}>
                Go Back to Customers
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { customer, bookings, bookingStats, spendingStats, serviceHistory } = profile;

  // Separate auto tags from manual tags
  const autoTags = ['frequente', 'super-frequente', 'alto-valor', 'bom-valor', 'ativo-recente', 'precisa-contato', 'multi-servico'];
  const customerAutoTags = customer.tags.filter(t => autoTags.includes(t));
  const customerManualTags = customer.tags.filter(t => !autoTags.includes(t));

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate({ to: '/operator/customers' })}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Customers
      </Button>

      {/* Customer Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                {customer.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
              </div>

              {/* Info */}
              <div className="space-y-2">
                {isEditingProfile ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        placeholder="Nome do cliente"
                        className="h-9 w-64 text-lg font-bold"
                        autoFocus
                      />
                      <Badge className={getTierColorClasses(customer.tierColor)}>{customer.tierLabel}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <Input
                        value={editedEmail}
                        onChange={(e) => setEditedEmail(e.target.value)}
                        placeholder="email@exemplo.com"
                        className="h-8 w-64 text-sm"
                        type="email"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setIsEditingProfile(false); }}>
                        <XCircle className="h-4 w-4 mr-1" />
                        Cancelar
                      </Button>
                      <Button variant="ghost" size="sm" onClick={handleSaveProfile} disabled={savingProfile || !editedName.trim()}>
                        {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                        Salvar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <h1 className="text-2xl font-bold">
                        {canViewFullPII ? customer.name : `${customer.name.split(' ')[0]} ${customer.name.split(' ')[1]?.charAt(0) || ''}.`}
                      </h1>
                      <Badge className={getTierColorClasses(customer.tierColor)}>{customer.tierLabel}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => {
                          setEditedName(customer.name || '');
                          setEditedEmail(customer.email || '');
                          setIsEditingProfile(true);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Phone className="h-4 w-4" />
                        {canViewFullPII ? customer.phone : maskPhone(customer.phone, false)}
                      </div>
                      {customer.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-4 w-4" />
                          {canViewFullPII ? customer.email : '***@***.com'}
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Cliente desde {formatDate(customer.firstBookingAt)}
                      </div>
                    </div>
                  </>
                )}

                {/* Engagement Score */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Engagement:</span>
                  <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${customer.engagementScore >= 75 ? 'bg-green-500' : customer.engagementScore >= 50 ? 'bg-blue-500' : customer.engagementScore >= 25 ? 'bg-orange-500' : 'bg-red-500'}`}
                      style={{ width: `${customer.engagementScore}%` }}
                    />
                  </div>
                  <span className={`text-sm font-semibold ${getEngagementColor(customer.engagementScore)}`}>
                    {customer.engagementScore}%
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <MessageSquare className="h-4 w-4 mr-2" />
                Enviar Mensagem
              </Button>
              <Button size="sm">
                <Calendar className="h-4 w-4 mr-2" />
                Novo Agendamento
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Financial Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Total Gasto</p>
                    <p className="text-lg font-bold">{formatCurrency(spendingStats.totalSpent)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Media por Visita</p>
                    <p className="text-lg font-bold">{formatCurrency(spendingStats.averagePerVisit)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-purple-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Total Agendamentos</p>
                    <p className="text-lg font-bold">{bookingStats.total}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Ultima Visita</p>
                    <p className="text-lg font-bold">{formatDate(customer.lastBookingAt)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking History */}
          <Card>
            <CardHeader>
              <CardTitle>Historico de Agendamentos</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="all">
                <TabsList className="mb-4">
                  <TabsTrigger value="all">Todos ({bookingStats.total})</TabsTrigger>
                  <TabsTrigger value="upcoming">Proximos ({bookingStats.upcoming})</TabsTrigger>
                  <TabsTrigger value="completed">Concluidos ({bookingStats.completed})</TabsTrigger>
                  <TabsTrigger value="cancelled">Cancelados ({bookingStats.cancelled})</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <BookingTable bookings={bookings} formatDateTime={formatDateTime} formatCurrency={formatCurrency} />
                </TabsContent>
                <TabsContent value="upcoming">
                  <BookingTable
                    bookings={bookings.filter(b => b.scheduledAt > new Date() && ['PENDING', 'CONFIRMED'].includes(b.status))}
                    formatDateTime={formatDateTime}
                    formatCurrency={formatCurrency}
                  />
                </TabsContent>
                <TabsContent value="completed">
                  <BookingTable
                    bookings={bookings.filter(b => b.status === 'COMPLETED')}
                    formatDateTime={formatDateTime}
                    formatCurrency={formatCurrency}
                  />
                </TabsContent>
                <TabsContent value="cancelled">
                  <BookingTable
                    bookings={bookings.filter(b => ['CANCELLED', 'NO_SHOW'].includes(b.status))}
                    formatDateTime={formatDateTime}
                    formatCurrency={formatCurrency}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Service History */}
          {serviceHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Servicos Mais Utilizados</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {serviceHistory.slice(0, 5).map((service) => (
                    <div key={service.serviceName} className="flex items-center justify-between">
                      <span className="font-medium">{service.serviceName}</span>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{service.count}x</span>
                        <span>Ultimo: {formatDate(service.lastUsed)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* Notes & Preferences */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Notas</CardTitle>
              {!isEditingNotes ? (
                <Button variant="ghost" size="sm" onClick={() => setIsEditingNotes(true)}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setIsEditingNotes(false); setEditedNotes(customer.notes || ''); }}>
                    <XCircle className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSaveNotes} disabled={savingNotes}>
                    {savingNotes ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {isEditingNotes ? (
                <Textarea
                  value={editedNotes}
                  onChange={(e) => setEditedNotes(e.target.value)}
                  placeholder="Adicione notas sobre o cliente..."
                  rows={4}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  {customer.notes || 'Nenhuma nota adicionada.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tags</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto Tags */}
              {customerAutoTags.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Automaticas</p>
                  <div className="flex flex-wrap gap-1">
                    {customerAutoTags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Tags */}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Personalizadas</p>
                <div className="flex flex-wrap gap-1">
                  {customerManualTags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs flex items-center gap-1">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {isAddingTag ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                        placeholder="Nova tag..."
                        className="h-6 w-24 text-xs"
                        autoFocus
                      />
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleAddTag}>
                        <CheckCircle className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setIsAddingTag(false); setNewTag(''); }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setIsAddingTag(true)}>
                      <Plus className="h-3 w-3 mr-1" />
                      Adicionar
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Booking Stats Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Agendamentos concluidos</span>
                  <span className="font-medium">{bookingStats.completed}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cancelamentos</span>
                  <span className="font-medium">{bookingStats.cancelled}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nao compareceu</span>
                  <span className="font-medium">{bookingStats.noShow}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Proximos agendamentos</span>
                  <span className="font-medium">{bookingStats.upcoming}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Booking Table Component
function BookingTable({
  bookings,
  formatDateTime,
  formatCurrency
}: {
  bookings: BookingListItemClient[];
  formatDateTime: (date: Date) => string;
  formatCurrency: (amount: number) => string;
}) {
  if (bookings.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhum agendamento encontrado.
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data/Hora</TableHead>
            <TableHead>Servico</TableHead>
            <TableHead>Profissional</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Valor</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bookings.slice(0, 10).map((booking) => {
            const statusBadge = getStatusBadge(booking.status);
            return (
              <TableRow key={booking.id}>
                <TableCell className="font-medium">
                  {formatDateTime(booking.scheduledAt)}
                </TableCell>
                <TableCell>{booking.serviceName}</TableCell>
                <TableCell>{booking.staffName || '-'}</TableCell>
                <TableCell>
                  <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  {formatCurrency(booking.totalPrice)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/customers/$id')({
  component: CustomerProfilePage,
});
